import { AxiosError, AxiosResponse } from 'axios';

export type TApiEquipmentLocation = {
  title: string;
};

export type TApiEquipmentDepartment = {
  title: string;
};

export interface TApiEquipmentCategory {
  id: number;
  nameDa: string;
  nameEn: string;
  orderNr: number;
  parentId: number;
}

export enum EquipmentFileType {
  INSTRUCTION = 1,
  DOCUMENT = 2,
}

export type TApiEquipmentFile = {
  id: number;
  media: string;
  published: boolean;
  title?: string;
  notes?: string;
  type: EquipmentFileType;
};

export type TApiEquipmentSymbol = {
  label: string;
  description: string;
  image: string;
};

export type TApiEquipmentSupplier = {
  id: number;
  name: string;
};

export type TApiServiceConclusion = {
  status: string;
  statusValue: number;
  date: string;
  user: string;
};

export type TApiEquipmentEntity = {
  id: number;
  name: string;
  model?: string;
  serialVin?: string;
  description?: string;
  instructions?: string;
  licenseNumber?: string;
  internalId?: string;
  ceMarking?: boolean;
  warrantyExpires?: Date;
  lastRevised?: Date;
  manufacturer?: TApiEquipmentSupplier;
  supplier?: TApiEquipmentSupplier;
  purchaseDate?: Date;
  image: string;
  updateDate: Date;
  locations: Array<TApiEquipmentLocation>;
  departments: Array<TApiEquipmentDepartment>;
  category: TApiEquipmentCategory;
  equipmentFiles: Array<TApiEquipmentFile>;
};

export interface TEquipmentState {
  readonly loading?: boolean;
  readonly data?: Array<TApiEquipmentEntity>;
  readonly activeEquipment?: {
    data: TApiEquipmentEntity;
    ppe: Array<TApiEquipmentSymbol>;
    prohibitions: Array<TApiEquipmentSymbol>;
    latestService?: TApiServiceConclusion;
    nextServiceDate?: string;
  };
  readonly filteredList?: Array<TApiEquipmentEntity>;
  readonly categories?: Array<TApiEquipmentCategory>;
  readonly searchPhrase?: string;
}

export const initialEquipmentState: TEquipmentState = {};

export enum EquipmentCategories {
  REQUEST = 'EQUIPMENT_CATEGORIES_REQUEST',
  SUCCESS = 'EQUIPMENT_CATEGORIES_SUCCESS',
  FAILED = 'EQUIPMENT_CATEGORIES_FAILED',
}

export type TGetEquipmentCategories =
  | {
      type: EquipmentCategories.REQUEST;
    }
  | {
      type: EquipmentCategories.SUCCESS;
      payload: AxiosResponse<{ categories: Array<TApiEquipmentCategory> }>;
    }
  | {
      type: EquipmentCategories.FAILED;
      payload: AxiosError;
    };

export enum EquipmentList {
  REQUEST = 'EQUIPMENT_LIST_REQUEST',
  SUCCESS = 'EQUIPMENT_LIST_SUCCESS',
  FAILED = 'EQUIPMENT_LIST_FAILED',
}

export type TGetEquipmentList =
  | {
      type: EquipmentList.REQUEST;
    }
  | {
      type: EquipmentList.SUCCESS;
      payload: AxiosResponse<{ equipment: Array<TApiEquipmentEntity> }>;
    }
  | {
      type: EquipmentList.FAILED;
      payload: AxiosError;
    };

export enum EquipmentItem {
  REQUEST = 'EQUIPMENT_ITEM_REQUEST',
  SUCCESS = 'EQUIPMENT_ITEM_SUCCESS',
  FAILED = 'EQUIPMENT_ITEM_FAILED',
}

export type TGetEquipmentItem =
  | {
      type: EquipmentItem.REQUEST;
    }
  | {
      type: EquipmentItem.SUCCESS;
      payload: AxiosResponse<{
        equipment: TApiEquipmentEntity;
        ppe: Array<TApiEquipmentSymbol>;
        prohibitions: Array<TApiEquipmentSymbol>;
        latestService?: TApiServiceConclusion;
        nextServiceDate?: string;
      }>;
    }
  | {
      type: EquipmentItem.FAILED;
      payload: AxiosError;
    };

export enum EquipmentImageUpload {
  REQUEST = 'EQUIPMENT_IMAGE_UPLOAD_REQUEST',
  SUCCESS = 'EQUIPMENT_IMAGE_UPLOAD_SUCCESS',
  FAILED = 'EQUIPMENT_IMAGE_UPLOAD_FAILED',
}

export type TApiEntityImage = {
  image: string;
};

export type TUploadEquipmentImage =
  | {
      type: EquipmentImageUpload.REQUEST;
    }
  | {
      type: EquipmentImageUpload.SUCCESS;
      payload: AxiosResponse<TApiEntityImage>;
    }
  | {
      type: EquipmentImageUpload.FAILED;
      payload: AxiosError;
    };

export enum FilterEquipmentList {
  SUBMIT = 'FILTER_EQUIPMENT_LIST',
}

export type TFilterEquipmentListActionPayload = {
  search: string;
  location?: string;
};

export type TFilterEquipmentListAction = {
  type: FilterEquipmentList.SUBMIT;
  payload: TFilterEquipmentListActionPayload;
};

export type TEquipmentActions =
  | TGetEquipmentCategories
  | TGetEquipmentList
  | TGetEquipmentItem
  | TUploadEquipmentImage
  | TFilterEquipmentListAction;
